import { ColumnsType } from 'antd/es/table';
import { PrettyCurrency, Typography } from 'components';
import { ORDERS_CUSTOMIZATION_COLUMNS_PERSISTED_STATE } from 'constants/persistedStates';
import { Order, PurchaseOrder, PurchaseOrderPage } from 'gql/graphql';
import { sneakyCase } from 'helpers/sneakyCase';
import usePersistentContext from 'hooks/usePersistenceState';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { dateTimeShort } from 'utils/time/dateTimeShort';
import DescriptionOrder from '../_components/DescriptionOrder';
import LanguagePairsOrder from '../_components/LanguagePairsOrder';
import TotalAmountOrder from '../_components/TotalAmount';

enum OrdersKeysEnum {
  Id = 'id',
  Description = 'description',
  LanguagePair = 'languagePair',
  Words = 'words',
  Quality = 'quality',
  Amount = 'amount',
  TotalAmount = 'totalAmount',
  CreatedAt = 'createdAt'
}

export const ORDERS_KEYS_MAP = {
  [OrdersKeysEnum.Id]: OrdersKeysEnum.Id,
  [OrdersKeysEnum.Description]: OrdersKeysEnum.Description,
  [OrdersKeysEnum.LanguagePair]: sneakyCase(OrdersKeysEnum.LanguagePair),
  [OrdersKeysEnum.Words]: OrdersKeysEnum.Words,
  [OrdersKeysEnum.Quality]: OrdersKeysEnum.Quality,
  [OrdersKeysEnum.Amount]: OrdersKeysEnum.Amount,
  [OrdersKeysEnum.TotalAmount]: sneakyCase(OrdersKeysEnum.TotalAmount),
  [OrdersKeysEnum.CreatedAt]: sneakyCase(OrdersKeysEnum.CreatedAt)
};

export interface PurchaseOrderUnitary extends PurchaseOrder {
  order: Order;
}

interface IUseTableOrdersIn {
  orders?: PurchaseOrderPage;
}

interface IUseTableOrdersOut {
  columns: ColumnsType<PurchaseOrderUnitary>;
  columnsKeys: string[];
  columnsKeysFiltered: string[];
  handleOnClickBadge: (key: string) => () => void;
  ORDERS_KEYS_MAP: typeof ORDERS_KEYS_MAP;
  ordersUnitary: PurchaseOrderUnitary[];
}

export default function useTableOrders({ orders }: IUseTableOrdersIn): IUseTableOrdersOut {
  const t = useTranslations();

  const [customizationColumns, setCustomizationTable] = usePersistentContext(
    ORDERS_CUSTOMIZATION_COLUMNS_PERSISTED_STATE
  );

  const ordersUnitary = useMemo(
    () =>
      orders?.nodes?.reduce(
        (acc, item) => [...acc, ...item.orders.map((order) => ({ ...item, order }))],
        [] as PurchaseOrderUnitary[]
      ) ?? [],
    [orders]
  );

  const columnsBulk: ColumnsType<PurchaseOrderUnitary> = useMemo(
    () => [
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.Id]}` as any),
        dataIndex: OrdersKeysEnum.Id,
        key: OrdersKeysEnum.Id,
        render: (_, purchaseOrder) => (
          <Typography variant="regular4" className="select-none  text-tertiary-100">
            {purchaseOrder.id}
          </Typography>
        )
      },
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.Description]}` as any),
        dataIndex: OrdersKeysEnum.Description,
        key: OrdersKeysEnum.Description,
        render: (_, purchaseOrder) => <DescriptionOrder purchaseOrder={purchaseOrder} />,
        onCell: (purchaseOrder) => ({
          rowSpan:
            purchaseOrder.orders?.at(0)?.id === purchaseOrder.order.id
              ? purchaseOrder.orders.length
              : 0
        })
      },
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.LanguagePair]}` as any),
        dataIndex: OrdersKeysEnum.LanguagePair,
        key: OrdersKeysEnum.LanguagePair,
        render: (_, purchaseOrder) => <LanguagePairsOrder purchaseOrder={purchaseOrder} />
      },
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.Words]}` as any),
        dataIndex: OrdersKeysEnum.Words,
        key: OrdersKeysEnum.Words,
        render: (_, purchaseOrder) => (
          <Typography variant="regular4" className="select-none  text-tertiary-100">
            {purchaseOrder.order.totalOrderedWordCount}
          </Typography>
        )
      },
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.Quality]}` as any),
        dataIndex: OrdersKeysEnum.Quality,
        key: OrdersKeysEnum.Quality,
        render: (_, purchaseOrder) => (
          <Typography variant="regular4" className="select-none  text-tertiary-100">
            {t(`qualities.${purchaseOrder.order.quality}` as any)}
          </Typography>
        )
      },
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.Amount]}` as any),
        dataIndex: OrdersKeysEnum.Amount,
        key: OrdersKeysEnum.Amount,
        render: (_, purchaseOrder) => (
          <Typography variant="regular4" className="select-none text-tertiary-100">
            <PrettyCurrency
              className="!font-normal"
              amount={purchaseOrder.order.price}
              currency={purchaseOrder.order.currency}
            />
          </Typography>
        )
      },
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.TotalAmount]}` as any),
        dataIndex: OrdersKeysEnum.TotalAmount,
        key: OrdersKeysEnum.TotalAmount,
        render: (_, purchaseOrder) => <TotalAmountOrder purchaseOrder={purchaseOrder} />,
        onCell: (purchaseOrder) => ({
          rowSpan:
            purchaseOrder.orders?.at(0)?.id === purchaseOrder.order.id
              ? purchaseOrder.orders.length
              : 0
        })
      },
      {
        title: t(`billing.orders.table.${ORDERS_KEYS_MAP[OrdersKeysEnum.CreatedAt]}` as any),
        dataIndex: OrdersKeysEnum.CreatedAt,
        key: OrdersKeysEnum.CreatedAt,
        render: (_, purchaseOrder) => (
          <Typography variant="regular4" className="select-none text-tertiary-100">
            {dateTimeShort(purchaseOrder.createdAt, {
              includeYear: true,
              showYesterday: true,
              t: t as any
            })}
          </Typography>
        ),
        onCell: (purchaseOrder) => ({
          rowSpan:
            purchaseOrder.orders?.at(0)?.id === purchaseOrder.order.id
              ? purchaseOrder.orders.length
              : 0
        })
      }
    ],
    [t]
  );

  const columns = columnsBulk.filter((column) =>
    customizationColumns ? (customizationColumns as string[]).includes(column.key as string) : true
  );

  const columnsKeys = columnsBulk.map((column) => column.key as string);

  const columnsKeysHidden = useMemo(() => [OrdersKeysEnum.Id, OrdersKeysEnum.CreatedAt], []);

  const columnsKeysFiltered = columnsKeys.filter((column) =>
    customizationColumns ? (customizationColumns as string[]).includes(column) : true
  );

  const handleOnClickBadge = (key: string) => () => {
    if (columnsKeysFiltered.includes(key))
      return setCustomizationTable(columnsKeysFiltered.filter((colKey) => colKey !== key));

    return setCustomizationTable([...columnsKeysFiltered, key]);
  };

  useEffect(() => {
    setTimeout(() => {
      if (!customizationColumns || !(customizationColumns as any)?.length) {
        const columnsKeysFiltered = columns
          .map((column) => column.key as OrdersKeysEnum)
          .filter((key) => !columnsKeysHidden.includes(key));

        setCustomizationTable(columnsKeysFiltered);
      }
    }, 3000);
  }, [columns, columnsKeysHidden, customizationColumns, setCustomizationTable]);

  return {
    ordersUnitary,
    columns,
    columnsKeys,
    columnsKeysFiltered,
    handleOnClickBadge,
    ORDERS_KEYS_MAP
  };
}
